import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Autocomplete,
  Box,
  Avatar,
  Chip,
  FormControlLabel,
  Switch,
  Collapse,
  Divider,
  Tooltip,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { user as userApi } from '../../../utils/api';
import { useAuth } from '../../../components/auth/AuthModule';

const CreateChannelDialog = ({ open, onClose, onCreate }) => {
  const { t } = useTranslation(['common']);
  const { user: currentUser } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isRestrictedAccess, setIsRestrictedAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errors, setErrors] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    // Reset form when dialog opens/closes
    if (open) {
      resetForm();
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    if (open) {
      try {
        setLoadingUsers(true);
        console.log('Fetching users for channel creation');
        const response = await userApi.getUsers();
        console.log('User API response:', response);
        
        // Handle the response correctly - response is now directly the array of users
        const users = Array.isArray(response) ? response : [];
        
        // Filter out current user as they'll be added automatically
        const filteredUsers = users.filter(user => user._id !== currentUser?._id);
        setAllUsers(filteredUsers);
        console.log('Available users set:', filteredUsers.length);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setIsRestrictedAccess(false);
    setSelectedUsers([]);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = t('comms.nameRequired');
    } else if (name.trim().length < 2) {
      newErrors.name = t('comms.nameTooShort');
    } else if (name.trim().length > 30) {
      newErrors.name = t('comms.nameTooLong');
    }
    
    if (description.length > 100) {
      newErrors.description = t('comms.descriptionTooLong');
    }
    
    if (isRestrictedAccess && selectedUsers.length === 0) {
      newErrors.members = t('comms.restrictedMembersRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const channelData = {
        name: name.trim(),
        description: description.trim(),
        type: 'team',  // Always create team type channels
        members: isRestrictedAccess ? selectedUsers.map(user => user._id) : [], // Only specify members for restricted access
        isRestrictedAccess: isRestrictedAccess // Add a flag to indicate restricted access
      };
      
      await onCreate(channelData);
      handleClose();
    } catch (error) {
      console.error('Error creating channel:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestrictedAccessToggle = (event) => {
    setIsRestrictedAccess(event.target.checked);
    // Reset selected users when toggling off restricted access
    if (!event.target.checked) {
      setSelectedUsers([]);
      setErrors({...errors, members: null});
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t('comms.createNewChannel')}
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('comms.createChannelDescription')}
        </Typography>
        
        <TextField
          autoFocus
          margin="dense"
          label={t('comms.channelName')}
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          disabled={loading}
          placeholder={t('comms.channelNamePlaceholder')}
          sx={{ mb: 2 }}
        />
        
        <TextField
          margin="dense"
          label={t('comms.description')}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={!!errors.description}
          helperText={errors.description || t('comms.descriptionHelp')}
          disabled={loading}
          placeholder={t('comms.descriptionPlaceholder')}
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />
        
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isRestrictedAccess}
                onChange={handleRestrictedAccessToggle}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Tooltip title={t('comms.restrictedChannelTooltip')}>
                <Typography variant="body2">
                  {t('comms.restrictedChannel')}
                </Typography>
              </Tooltip>
            }
          />
          
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {isRestrictedAccess 
              ? t('comms.restrictedChannelDescription') 
              : t('comms.teamChannelDescription')}
          </Typography>
        </Box>

        {isRestrictedAccess && (
          <>
            <Divider sx={{ my: 2 }} />
            
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('comms.restrictedChannelAlert')}
            </Alert>
            
            <FormControl fullWidth sx={{ mt: 2 }} error={!!errors.members}>
              <Autocomplete
                multiple
                id="members-select"
                options={allUsers}
                loading={loadingUsers}
                disabled={loading || loadingUsers}
                value={selectedUsers}
                onChange={(event, newValue) => {
                  setSelectedUsers(newValue);
                  if (newValue.length > 0 && errors.members) {
                    setErrors({...errors, members: null});
                  }
                }}
                getOptionLabel={(option) => option.name || option.username || ''}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                noOptionsText={
                  loadingUsers
                    ? t('comms.loadingUsers')
                    : allUsers.length === 0
                      ? t('comms.noAvailableUsers')
                      : t('comms.noMatchingUsers')
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={option.imageUrl} 
                        alt={option.name || option.username}
                        sx={{ width: 24, height: 24, mr: 1 }}
                      >
                        {(option.name || option.username || '?')[0].toUpperCase()}
                      </Avatar>
                      <Typography>{option.name || option.username}</Typography>
                    </Box>
                  </li>
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      key={option._id}
                      avatar={
                        <Avatar 
                          src={option.imageUrl}
                          alt={option.name || option.username}
                        >
                          {(option.name || option.username || '?')[0].toUpperCase()}
                        </Avatar>
                      }
                      label={option.name || option.username}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('comms.selectMembers')}
                    placeholder={t('comms.selectMembers')}
                    error={!!errors.members}
                    helperText={errors.members || t('comms.selectMembersHelp')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading || !name.trim() || (isRestrictedAccess && selectedUsers.length === 0)}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('creating') : t('create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateChannelDialog; 